import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';

import BlueGroupIcon from '../icons/BlueGroupIcon';
import OrangeGroupIcon from '../icons/OrangeGroupIcon';
import GroupActionsMenu from './GroupActionsMenu';
import { LAST_ACCESS_FORMAT, ownerName } from './GroupCard';
import { CARD, KICKER, PILL_PRIMARY, PILL_SECONDARY, cx } from '../../pages/Home/HomeDashboard/dashboardStyles';
import { REDESIGN_COLORS, REDESIGN_FONTS, REDESIGN_TINTS } from '../../constants';

// Below this the owner and date columns fold under the name.
const NARROW = '@media (max-width: 860px)';

const useStyles = makeStyles(() => ({
    list: {
        ...CARD,
        overflow: 'hidden',
    },
    // Shared by the header and every row so the columns line up. Each row is its
    // own grid, so the actions column is fixed: with `auto` a "start lesson" row
    // and a "join" row got different widths and the owner and date columns shifted.
    grid: {
        display: 'grid',
        gridTemplateColumns: '48px minmax(0, 2fr) minmax(0, 1.3fr) minmax(0, 1.3fr) 240px',
        alignItems: 'center',
        gap: '16px',
        padding: '14px 22px',
        [NARROW]: {
            gridTemplateColumns: '44px minmax(0, 1fr) auto',
            gap: '12px',
            padding: '14px 16px',
        },
    },
    head: {
        borderBottom: `1px solid ${REDESIGN_COLORS.sand}`,
        background: REDESIGN_COLORS.cream,
        [NARROW]: {
            display: 'none',
        },
    },
    headCell: KICKER,
    row: {
        borderBottom: `1px solid ${REDESIGN_COLORS.sand}`,
        transition: 'background 120ms ease',
        '&:last-child': {
            borderBottom: 0,
        },
        '&:hover': {
            background: REDESIGN_COLORS.cream,
        },
    },
    locked: {
        background: REDESIGN_COLORS.amberSurface,
        '&:hover': {
            background: REDESIGN_COLORS.amberSurface,
        },
    },
    disc: {
        display: 'grid',
        placeItems: 'center',
        width: '48px',
        height: '48px',
        borderRadius: '16px',
        [NARROW]: {
            width: '44px',
            height: '44px',
            borderRadius: '14px',
        },
    },
    discMine: {
        background: REDESIGN_COLORS.mist,
    },
    discGuest: {
        background: REDESIGN_TINTS.peach,
    },
    groupIcon: {
        width: '28px',
        height: '16px',
    },
    main: {
        minWidth: 0,
    },
    name: {
        margin: 0,
        fontSize: '1.02rem',
        lineHeight: 1.3,
        letterSpacing: '-0.015em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
        overflowWrap: 'anywhere',
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        marginTop: '6px',
    },
    chip: {
        display: 'inline-block',
        padding: '3px 8px',
        borderRadius: '999px',
        fontSize: '0.66rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
    },
    chipMine: {
        color: REDESIGN_COLORS.link,
        background: REDESIGN_COLORS.mist,
    },
    chipGuest: {
        color: REDESIGN_COLORS.accent,
        background: REDESIGN_TINTS.peach,
    },
    chipAmber: {
        color: REDESIGN_COLORS.dark,
        background: REDESIGN_COLORS.amberBadge,
    },
    // On phones the owner and the date read as one meta line under the name.
    meta: {
        display: 'none',
        marginTop: '6px',
        color: REDESIGN_COLORS.body,
        fontSize: '0.84rem',
        [NARROW]: {
            display: 'block',
        },
    },
    cell: {
        minWidth: 0,
        color: REDESIGN_COLORS.body,
        fontSize: '0.9rem',
        [NARROW]: {
            display: 'none',
        },
    },
    // Its own display beats the cell's phone rule, so it repeats it.
    owner: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontWeight: REDESIGN_FONTS.weightStrong,
        color: REDESIGN_COLORS.dark,
        [NARROW]: {
            display: 'none',
        },
    },
    ownerText: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    avatar: {
        width: '28px',
        height: '28px',
        fontSize: '0.8rem',
        background: REDESIGN_COLORS.line,
        color: REDESIGN_COLORS.dark,
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '8px',
    },
    start: {
        ...PILL_PRIMARY,
        minHeight: '40px',
        padding: '0 16px',
        fontSize: '0.88rem',
    },
    join: {
        ...PILL_SECONDARY,
        minHeight: '40px',
        padding: '0 16px',
        fontSize: '0.88rem',
    },
    // On phones the button label makes the row too wide; keep the arrow only.
    label: {
        [NARROW]: {
            display: 'none',
        },
    },
}));

/**
 * The groups as a list: one row per group with the owner, the last access,
 * the main action and the menu of secondary actions, under a column header
 * on wide screens. `actionsFor(row)` returns the menu entries for a row.
 */
const GroupList = ({ groups, profile, onStart, actionsFor }) => {
    const classes = useStyles();
    const intl = useIntl();

    return (
        <div className={classes.list} role="table">
            <div className={cx(classes.grid, classes.head)} role="row">
                <span />
                <span className={classes.headCell} role="columnheader">
                    <FormattedMessage id="redesign.generic.label.name" />
                </span>
                <span className={classes.headCell} role="columnheader">
                    <FormattedMessage id="redesign.generic.label.owner" />
                </span>
                <span className={classes.headCell} role="columnheader">
                    <FormattedMessage id="redesign.generic.label.lastAccessed" />
                </span>
                <span />
            </div>

            {groups.map((row) => {
                const mine = !!row.isMine;
                const targetId = mine ? row.id : row.classId;
                const requests = mine ? Number(row.requests) || 0 : 0;
                const owner = mine ? profile.name : ownerName(row);
                const avatar = mine ? profile.avatar : row.avatar;
                const locked = row.enabled === false;
                const lastUsed = intl.formatDate(row.lastaccess || row.updatedAt, LAST_ACCESS_FORMAT);

                return (
                    <div
                        key={`${mine ? 'mine' : 'guest'}-${targetId}`}
                        className={cx(classes.grid, classes.row, locked && classes.locked)}
                        role="row"
                    >
                        <span aria-hidden="true" className={cx(classes.disc, mine ? classes.discMine : classes.discGuest)}>
                            {mine ? (
                                <BlueGroupIcon className={classes.groupIcon} />
                            ) : (
                                <OrangeGroupIcon className={classes.groupIcon} />
                            )}
                        </span>

                        <div className={classes.main} role="cell">
                            <h3 className={classes.name}>{row.name}</h3>
                            <div className={classes.chips}>
                                <span className={cx(classes.chip, mine ? classes.chipMine : classes.chipGuest)}>
                                    <FormattedMessage
                                        id={mine ? 'redesign.home.dashboard.recent.mine' : 'redesign.home.dashboard.recent.guest'}
                                    />
                                </span>
                                {requests > 0 ? (
                                    <span className={cx(classes.chip, classes.chipAmber)}>
                                        <FormattedMessage
                                            id="redesign.home.dashboard.recent.requests"
                                            values={{ count: requests }}
                                        />
                                    </span>
                                ) : null}
                                {locked ? (
                                    <span className={cx(classes.chip, classes.chipAmber)}>
                                        <FormattedMessage id="redesign.groups.locked" />
                                    </span>
                                ) : null}
                            </div>
                            <p className={classes.meta}>
                                {owner} · {lastUsed}
                            </p>
                        </div>

                        <div className={cx(classes.cell, classes.owner)} role="cell">
                            <Avatar src={avatar || undefined} alt="" className={classes.avatar}>
                                {(owner || '?').charAt(0).toUpperCase()}
                            </Avatar>
                            <span className={classes.ownerText}>{owner}</span>
                        </div>

                        <div className={classes.cell} role="cell">
                            {lastUsed}
                        </div>

                        <div className={classes.actions} role="cell">
                            <button
                                type="button"
                                onClick={() => onStart(targetId)}
                                aria-label={intl.formatMessage({
                                    id: mine ? 'redesign.generic.label.start' : 'generic.label.join',
                                })}
                                className={mine ? classes.start : classes.join}
                            >
                                <span className={classes.label}>
                                    <FormattedMessage id={mine ? 'redesign.generic.label.start' : 'generic.label.join'} />
                                </span>
                                <span aria-hidden="true">→</span>
                            </button>
                            <GroupActionsMenu row={row} actions={actionsFor ? actionsFor(row) : []} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default GroupList;
